import { create } from 'zustand';
import { Howl } from 'howler';
import type { Audio } from '../types/story.types';

interface AudioInstance {
  howl: Howl;
  url: string;
  id: string;
  baseVolume?: number; // Store the original volume from config (0.0-1.0)
}

interface AudioState {
  // === Music ===
  currentMusic: AudioInstance | null;
  isMusicPlaying: boolean;
  isMusicLoading: boolean;
  musicProgress: number; // 0-100

  // === Voice (narrative audio) ===
  currentVoice: AudioInstance | null;
  isVoicePlaying: boolean;

  // === Sound Effects ===
  activeSFX: Map<string, AudioInstance>;

  // === Volume Controls (0-100) ===
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  voiceVolume: number;

  // === Ducking State ===
  isDucking: boolean;
  duckingLevel: number; // 0-100, percentage to duck to (e.g., 30 = 30% of original volume)
  originalMusicVolume: number | null; // Store original volume when ducking

  // === Global State ===
  isMuted: boolean;
  isPaused: boolean;

  // === Music Actions ===
  playMusic: (url: string, config?: Partial<Audio>) => void;
  stopMusic: () => void;
  pauseMusic: () => void;
  resumeMusic: () => void;
  crossfadeMusic: (url: string, duration: number, config?: Partial<Audio>) => void;

  // === Voice Actions ===
  playVoice: (url: string, config?: Partial<Audio>, onEnd?: () => void) => void;
  stopVoice: () => void;

  // === SFX Actions ===
  playSFX: (url: string, config?: Partial<Audio>, id?: string) => void;
  stopSFX: (id: string) => void;
  stopAllSFX: () => void;

  // === Volume Actions ===
  setMasterVolume: (vol: number) => void;
  setMusicVolume: (vol: number) => void;
  setSFXVolume: (vol: number) => void;
  setVoiceVolume: (vol: number) => void;
  toggleMute: () => void;

  // === Ducking Actions ===
  setDuckingLevel: (level: number) => void;
  duckMusic: () => void;
  unduckMusic: () => void;

  // === Global Actions ===
  pauseAll: () => void;
  resumeAll: () => void;
  stopAll: () => void;
  cleanup: () => void;
}

export const useAudioStore = create<AudioState>((set, get) => ({
  // === Initial State ===
  currentMusic: null,
  isMusicPlaying: false,
  isMusicLoading: false,
  musicProgress: 0,

  currentVoice: null,
  isVoicePlaying: false,

  activeSFX: new Map(),

  masterVolume: 80,
  musicVolume: 70,
  sfxVolume: 80,
  voiceVolume: 90,

  isDucking: false,
  duckingLevel: 30, // Duck music to 30% when voice is playing
  originalMusicVolume: null,

  isMuted: false,
  isPaused: false,

  // === Music Actions ===
  playMusic: (url, config = {}) => {
    const { currentMusic, stopMusic, musicVolume, masterVolume } = get();

    // Stop current music if playing
    if (currentMusic) {
      stopMusic();
    }

    const actualVolume =
      (config.volume ?? 1.0) * (musicVolume / 100) * (masterVolume / 100);
    const loop = config.loop ?? true; // Default music to loop
    const fadeInDuration = config.fadeInDuration ?? 0;

    set({ isMusicLoading: true });

    const music = new Howl({
      src: [url],
      loop,
      volume: fadeInDuration > 0 ? 0 : actualVolume,
      rate: config.playbackRate ?? 1.0,
      onload: () => {
        set({ isMusicLoading: false });

        // Apply fade in if specified
        if (fadeInDuration > 0) {
          music.fade(0, actualVolume, fadeInDuration);
        }
      },
      onplay: () => set({ isMusicPlaying: true }),
      onpause: () => set({ isMusicPlaying: false }),
      onend: () => set({ isMusicPlaying: false }),
      onstop: () => set({ isMusicPlaying: false }),
    });

    const instance: AudioInstance = {
      howl: music,
      url,
      id: `music-${Date.now()}`,
    };

    set({ currentMusic: instance });

    // Apply start delay if specified
    if (config.startDelay && config.startDelay > 0) {
      setTimeout(() => music.play(), config.startDelay);
    } else if (config.autoPlay !== false) {
      music.play();
    }
  },

  stopMusic: () => {
    const { currentMusic } = get();
    if (currentMusic) {
      currentMusic.howl.stop();
      currentMusic.howl.unload();
    }
    set({ currentMusic: null, isMusicPlaying: false });
  },

  pauseMusic: () => {
    const { currentMusic } = get();
    currentMusic?.howl.pause();
  },

  resumeMusic: () => {
    const { currentMusic } = get();
    currentMusic?.howl.play();
  },

  crossfadeMusic: (url, duration, config = {}) => {
    const { currentMusic, playMusic, musicVolume, masterVolume } = get();

    if (!currentMusic) {
      playMusic(url, config);
      return;
    }

    const currentVolume =
      (musicVolume / 100) * (masterVolume / 100);

    // Fade out current music
    currentMusic.howl.fade(currentVolume, 0, duration);

    // After fade completes, start new music
    setTimeout(() => {
      currentMusic.howl.stop();
      currentMusic.howl.unload();
      playMusic(url, config);
    }, duration);
  },

  // === Voice Actions ===
  playVoice: (url, config = {}, onEnd) => {
    const { currentVoice, stopVoice, voiceVolume, masterVolume, duckMusic, unduckMusic } = get();

    // Stop current voice if playing
    if (currentVoice) {
      stopVoice();
    }

    const actualVolume =
      (config.volume ?? 1.0) * (voiceVolume / 100) * (masterVolume / 100);

    const voice = new Howl({
      src: [url],
      loop: false,
      volume: actualVolume,
      rate: config.playbackRate ?? 1.0,
      onplay: () => {
        set({ isVoicePlaying: true });
        // Duck background music when voice starts
        duckMusic();
      },
      onend: () => {
        set({ isVoicePlaying: false, currentVoice: null });
        // Restore background music when voice ends
        unduckMusic();
        onEnd?.();
      },
      onstop: () => {
        set({ isVoicePlaying: false });
        // Restore background music if voice is stopped
        unduckMusic();
      },
    });

    const instance: AudioInstance = {
      howl: voice,
      url,
      id: `voice-${Date.now()}`,
    };

    set({ currentVoice: instance });

    // Apply start delay if specified
    if (config.startDelay && config.startDelay > 0) {
      setTimeout(() => voice.play(), config.startDelay);
    } else if (config.autoPlay !== false) {
      voice.play();
    }
  },

  stopVoice: () => {
    const { currentVoice } = get();
    if (currentVoice) {
      currentVoice.howl.stop();
      currentVoice.howl.unload();
    }
    set({ currentVoice: null, isVoicePlaying: false });
  },

  // === SFX Actions ===
  playSFX: (url, config = {}, id) => {
    const { activeSFX, sfxVolume, masterVolume } = get();
    const sfxId = id ?? `sfx-${Date.now()}-${Math.random()}`;

    const baseVolume = config.volume ?? 1.0; // Store the base volume from config
    const actualVolume = baseVolume * (sfxVolume / 100) * (masterVolume / 100);

    console.log('[AudioStore] playSFX - url:', url, 'baseVolume:', baseVolume, 'sfxVolume:', sfxVolume, 'masterVolume:', masterVolume, 'actualVolume:', actualVolume, 'loop:', config.loop, 'autoPlay:', config.autoPlay);

    const isLooping = config.loop ?? false;

    const sfx = new Howl({
      src: [url],
      loop: isLooping,
      volume: actualVolume,
      rate: config.playbackRate ?? 1.0,
      onload: () => {
        console.log('[AudioStore] SFX loaded:', sfxId, url);
      },
      onplay: () => {
        console.log('[AudioStore] SFX started playing:', sfxId, url);
      },
      onend: () => {
        console.log('[AudioStore] SFX ended:', sfxId, url, 'isLooping:', isLooping);
        // Only remove from active SFX if it's not looping
        // Looping sounds should stay in the map until explicitly stopped
        if (!isLooping) {
          const newActiveSFX = new Map(get().activeSFX);
          newActiveSFX.delete(sfxId);
          set({ activeSFX: newActiveSFX });
          console.log('[AudioStore] Removed non-looping SFX from map:', sfxId);
        }
      },
      onstop: () => {
        console.log('[AudioStore] SFX stopped:', sfxId, url);
      },
    });

    const instance: AudioInstance = {
      howl: sfx,
      url,
      id: sfxId,
      baseVolume, // Store the base volume for later adjustments
    };

    const newActiveSFX = new Map(activeSFX);
    newActiveSFX.set(sfxId, instance);
    set({ activeSFX: newActiveSFX });
    console.log('[AudioStore] Added SFX to activeSFX map:', sfxId, url, '- Total active:', newActiveSFX.size);

    // Apply start delay if specified
    if (config.startDelay && config.startDelay > 0) {
      setTimeout(() => sfx.play(), config.startDelay);
    } else if (config.autoPlay !== false) {
      sfx.play();
    }
  },

  stopSFX: (id) => {
    const { activeSFX } = get();
    const sfx = activeSFX.get(id);

    if (sfx) {
      sfx.howl.stop();
      sfx.howl.unload();
      const newActiveSFX = new Map(activeSFX);
      newActiveSFX.delete(id);
      set({ activeSFX: newActiveSFX });
    }
  },

  stopAllSFX: () => {
    const { activeSFX } = get();

    console.log('[AudioStore] stopAllSFX - stopping', activeSFX.size, 'active sound effects');

    activeSFX.forEach((instance, id) => {
      console.log('[AudioStore] Stopping SFX:', id, 'URL:', instance.url);
      instance.howl.stop();
      instance.howl.unload();
    });

    set({ activeSFX: new Map() });
    console.log('[AudioStore] stopAllSFX complete - all SFX stopped');
  },

  // === Volume Actions ===
  setMasterVolume: (vol) => {
    const {
      currentMusic,
      currentVoice,
      activeSFX,
      musicVolume,
      voiceVolume,
      sfxVolume,
    } = get();

    // Update all active audio
    if (currentMusic) {
      currentMusic.howl.volume((musicVolume / 100) * (vol / 100));
    }
    if (currentVoice) {
      currentVoice.howl.volume((voiceVolume / 100) * (vol / 100));
    }
    activeSFX.forEach((instance) => {
      // Apply the new master volume while preserving individual baseVolume
      const baseVolume = instance.baseVolume ?? 1.0;
      const actualVolume = baseVolume * (sfxVolume / 100) * (vol / 100);
      instance.howl.volume(actualVolume);
    });

    set({ masterVolume: vol });
  },

  setMusicVolume: (vol) => {
    const { currentMusic, masterVolume } = get();
    const actualVolume = (vol / 100) * (masterVolume / 100);
    currentMusic?.howl.volume(actualVolume);
    set({ musicVolume: vol });
  },

  setSFXVolume: (vol) => {
    const { activeSFX, masterVolume } = get();
    activeSFX.forEach((instance) => {
      // Apply the new SFX volume while preserving individual baseVolume
      const baseVolume = instance.baseVolume ?? 1.0;
      const actualVolume = baseVolume * (vol / 100) * (masterVolume / 100);
      instance.howl.volume(actualVolume);
    });
    set({ sfxVolume: vol });
  },

  setVoiceVolume: (vol) => {
    const { currentVoice, masterVolume } = get();
    const actualVolume = (vol / 100) * (masterVolume / 100);
    currentVoice?.howl.volume(actualVolume);
    set({ voiceVolume: vol });
  },

  toggleMute: () => {
    const { isMuted } = get();
    const newMuted = !isMuted;

    Howler.mute(newMuted);

    set({ isMuted: newMuted });
  },

  // === Ducking Actions ===
  setDuckingLevel: (level) => {
    set({ duckingLevel: level });
  },

  duckMusic: () => {
    const { currentMusic, isDucking, duckingLevel } = get();

    if (!currentMusic || isDucking) return;

    // Store the current volume
    const currentVol = currentMusic.howl.volume();
    set({ originalMusicVolume: currentVol, isDucking: true });

    // Calculate ducked volume
    const duckedVolume = currentVol * (duckingLevel / 100);

    // Fade to ducked volume over 300ms for smooth transition
    currentMusic.howl.fade(currentVol, duckedVolume, 300);

    console.log('[AudioStore] Ducking music from', currentVol, 'to', duckedVolume);
  },

  unduckMusic: () => {
    const { currentMusic, isDucking, originalMusicVolume } = get();

    if (!currentMusic || !isDucking || originalMusicVolume === null) return;

    const currentVol = currentMusic.howl.volume();

    // Fade back to original volume over 300ms
    currentMusic.howl.fade(currentVol, originalMusicVolume, 300);

    console.log('[AudioStore] Unducking music from', currentVol, 'to', originalMusicVolume);

    set({ isDucking: false, originalMusicVolume: null });
  },

  // === Global Actions ===
  pauseAll: () => {
    const { currentMusic, currentVoice, activeSFX } = get();

    currentMusic?.howl.pause();
    currentVoice?.howl.pause();
    activeSFX.forEach((instance) => instance.howl.pause());

    set({ isPaused: true });
  },

  resumeAll: () => {
    const { currentMusic, currentVoice, activeSFX, isMusicPlaying } = get();

    if (currentMusic && !isMusicPlaying) {
      currentMusic.howl.play();
    }

    currentVoice?.howl.play();
    activeSFX.forEach((instance) => instance.howl.play());

    set({ isPaused: false });
  },

  stopAll: () => {
    const { stopMusic, stopVoice, stopAllSFX } = get();

    stopMusic();
    stopVoice();
    stopAllSFX();
  },

  cleanup: () => {
    const { stopAll } = get();

    stopAll();

    set({
      currentMusic: null,
      currentVoice: null,
      activeSFX: new Map(),
      isPaused: false,
    });
  },
}));
